import * as React from "react";
import { CallbackPicker, useCallbackTime } from "@/features/callback-picker";
import { partiesOf, roleText } from "@/lib/accident";
import { alsoBookLine, alsoBookable, type AlsoBooking } from "@/lib/also-book";
import { knownZone } from "@/lib/callbacks";
import { contactName } from "@/lib/contacts";
import { useLeadAccident } from "@/lib/queries";
import type { AccidentRecord, Disposition, LeadCard } from "@/lib/types";
import { cn } from "@/lib/utils";

/**
 * Also book a callback for the others in the caller's accident (ACL-671, ADR-226 in the platform
 * repo), under the caller's own time in the outcome window and in Book a callback. Folded until the
 * rep opens it. A person ticked gets the same picker the caller has, on the caller's clock (the
 * accident's people are where the crash was); their callback is booked on their own record, through
 * the same route as the caller's, once the caller's is (`onChange` hands the bookings up). A person
 * on the caller's number shares the caller's call and is said so, never booked twice.
 */
export function AlsoBookFor({
  leadId, caller, outcome, disabled, onChange,
}: {
  leadId: string | null;
  caller: Pick<LeadCard, "phone" | "first_name" | "clock_timezone" | "timezone"> | undefined;
  /** The callback outcome being booked, for the time each picker opens on. */
  outcome: Pick<Disposition, "backoff_seconds"> | null;
  disabled?: boolean;
  onChange: (bookings: AlsoBooking[]) => void;
}) {
  const accident = useLeadAccident(leadId);
  const { bookable, shared } = alsoBookable(partiesOf(accident.data), caller?.phone);
  const [open, setOpen] = React.useState(false);
  const [rows, setRows] = React.useState<Record<string, AlsoBooking | null>>({});
  const tell = React.useRef(onChange);
  tell.current = onChange;
  const booked = React.useMemo(() => Object.values(rows).filter((b): b is AlsoBooking => b !== null), [rows]);
  React.useEffect(() => {
    tell.current(booked);
  }, [booked]);
  const onRow = React.useCallback((id: string, booking: AlsoBooking | null) => {
    setRows((now) => (now[id] === booking || (now[id]?.iso === booking?.iso && now[id]?.name === booking?.name) ? now : { ...now, [id]: booking }));
  }, []);
  if (bookable.length === 0 && shared.length === 0) return null;
  const first = caller?.first_name ?? "the caller";
  const zone = knownZone(caller?.clock_timezone ?? caller?.timezone);
  return (
    <fieldset className="rounded-md border border-border px-2.5 py-2" aria-label="Also book a callback for">
      <legend className="px-1 text-2xs font-semibold uppercase tracking-wide text-text-subtle">Also book a callback for</legend>
      <button
        type="button"
        className="flex w-full items-center justify-between gap-2 text-left text-xs"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className="text-text-muted">{alsoBookLine([...bookable, ...shared], booked.length)}</span>
        <span className="shrink-0 font-medium text-accent-11">{open ? "Hide" : "Book for them too"}</span>
      </button>
      {open && (
        <>
          <p className="mt-2 text-xs text-text-muted">
            Each at their own time, on {first}'s clock. Each callback goes on their own record and comes back to you as yours does.
          </p>
          <ul className="mt-2 space-y-3">
            {bookable.map((party) => (
              <BookingRow key={party.id} party={party} zone={zone} outcome={outcome} disabled={disabled} onRow={onRow} />
            ))}
            {shared.map((party) => (
              <li key={party.id} className="flex items-start gap-2 text-xs">
                <input type="checkbox" className="mt-0.5" checked disabled aria-label={`${contactName(party) ?? "No name"} shares the number`} />
                <span className="min-w-0 flex-1">
                  <span className="font-medium text-text">{contactName(party) ?? "No name"}</span>
                  <span className="text-text-muted"> · {roleText(party.role) ?? "in this accident"}</span>
                  <span className="mt-0.5 block text-text-muted">
                    Shares {first}'s number: one call reaches both, so {first}'s time is theirs too.
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
    </fieldset>
  );
}

/** One person with their own picker. The row says what it books, or nothing, whenever that changes. */
function BookingRow({
  party, zone, outcome, disabled, onRow,
}: {
  party: AccidentRecord;
  zone: string | null;
  outcome: Pick<Disposition, "backoff_seconds"> | null;
  disabled?: boolean;
  onRow: (id: string, booking: AlsoBooking | null) => void;
}) {
  const name = contactName(party) ?? "No name";
  const [ticked, setTicked] = React.useState(false);
  const time = useCallbackTime(zone);
  const { start } = time;
  const iso = time.value.iso;
  // Opens on the outcome's own wait, as the caller's picker does, the first time it is ticked.
  const started = React.useRef(false);
  React.useEffect(() => {
    if (ticked && !started.current) {
      started.current = true;
      start({ backoff_seconds: outcome?.backoff_seconds ?? null });
    }
  }, [ticked, start, outcome?.backoff_seconds]);
  React.useEffect(() => {
    onRow(party.id, ticked && iso ? { leadId: party.id, name, iso } : null);
  }, [ticked, iso, party.id, name, onRow]);
  return (
    <li className="text-xs">
      <label className="flex cursor-pointer items-start gap-2">
        <input
          type="checkbox"
          className="mt-0.5"
          checked={ticked}
          disabled={disabled}
          onChange={() => setTicked((v) => !v)}
          aria-label={`Also book ${name}`}
        />
        <span className="min-w-0 flex-1">
          <span className="font-medium text-text">{name}</span>
          <span className="text-text-muted"> · {roleText(party.role) ?? "in this accident"}</span>
          {party.callback && (
            <span className="block text-text-muted">Has a callback booked already; this one replaces it.</span>
          )}
        </span>
      </label>
      {ticked && (
        <div className={cn("mt-2 pl-6", disabled && "opacity-60")}>
          <CallbackPicker value={time.value} onChange={time.set} disabled={disabled} />
        </div>
      )}
    </li>
  );
}
